import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageSeo from "../components/PageSeo";
import config from "../assets/siteConfig.json";
import {
  authenticateAdmin,
  fetchAllBikesForAdmin,
  saveBikes,
  saveBikesToDatabase,
} from "../utils/bikesStorage";

const PageWrapper = styled.div`
  margin: 0 auto;
  padding: 0;
`;

const AdminContent = styled.section`
  max-width: ${({ theme }) => theme.maxWidth.prose};
  margin: 0 auto;
  padding: 2rem 1.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const PageTitle = styled.h1`
  font-size: clamp(1.5rem, 2.5vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 1.5rem;
`;

const BikeForm = styled.form`
  background: ${({ theme }) => theme.colors.bgMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: ${({ theme }) => theme.shadow.sm};
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }

  input,
  textarea {
    width: 100%;
    padding: 0.55rem 0.75rem;
    font-size: 1rem;
    font-family: inherit;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: ${({ theme }) => theme.radius.sm};
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.surface};
    transition: border-color ${({ theme }) => theme.transition};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  small {
    display: block;
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 0.85rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const AdminTools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin: 0 0 1rem;
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  border: none;
  color: #fff;
  padding: 0.6rem 1.2rem;
  font-size: 1rem;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: background ${({ theme }) => theme.transition};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  &.secondary {
    background-color: ${({ theme }) => theme.colors.textMuted};

    &:hover {
      background-color: ${({ theme }) => theme.colors.text};
    }
  }

  &.danger {
    background-color: #b42318;

    &:hover {
      background-color: #8a1c12;
    }
  }
`;

const BikeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BikeListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: 0.5rem;
  box-shadow: ${({ theme }) => theme.shadow.sm};

  .bike-info {
    flex: 1;
  }

  .bike-name {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .bike-price {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.95rem;
  }

  .bike-actions {
    display: flex;
    gap: 0.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 2rem 0 1rem;
`;

const EmptyBikes = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  padding: 1rem 0;
`;

const SoldBadge = styled.span`
  display: inline-block;
  background: #b42318;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
`;

const AvailableBadge = styled.span`
  display: inline-block;
  background: #16a34a;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
`;

const ToolMessage = styled.p`
  flex-basis: 100%;
  margin: 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LoginForm = styled.form`
  max-width: 320px;
  margin: 3rem auto;
  padding: 2rem;
  background: ${({ theme }) => theme.colors.bgMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.md};

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
  }

  input {
    width: 100%;
    padding: 0.55rem 0.75rem;
    font-size: 1rem;
    font-family: inherit;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: ${({ theme }) => theme.radius.sm};
    box-sizing: border-box;
    margin-bottom: 1rem;
    background: ${({ theme }) => theme.colors.surface};

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }

  .error {
    color: #b42318;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

const ADMIN_AUTH_KEY = 'basementbikemechanic_admin_auth';
const ADMIN_PASSWORD_KEY = 'basementbikemechanic_admin_password';
const FORM_DRAFT_KEY = 'basementbikemechanic_bike_form_draft';

const EMPTY_FORM = {
  name: "", images: [], price: "", description: "",
  frame_size: "", wheel_size: "", components: [],
};

function readDraft() {
  try {
    const raw = sessionStorage.getItem(FORM_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    return {
      form: { ...EMPTY_FORM, ...draft.form },
      componentInput: draft.componentInput || "",
      editingId: draft.editingId ?? null,
    };
  } catch {
    return null;
  }
}

function writeDraft(form, componentInput, editingId) {
  try {
    sessionStorage.setItem(
      FORM_DRAFT_KEY,
      JSON.stringify({ form, componentInput, editingId })
    );
  } catch {
    // Storage full or unavailable — silently ignore
  }
}

function clearDraft() {
  try {
    sessionStorage.removeItem(FORM_DRAFT_KEY);
  } catch {
    // ignore
  }
}

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 120px;
  padding: 1.5rem;
  box-sizing: border-box;
  border: 2px dashed ${({ theme, $dragging }) => $dragging ? theme.colors.primary : theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $dragging }) => $dragging ? theme.colors.accentMuted : theme.colors.surface};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transition}, background ${({ theme }) => theme.transition};
  text-align: center;
  font-size: 0.95rem;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.accentMuted};
  }

  .drop-icon {
    font-size: 2rem;
    line-height: 1;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }

  input {
    display: none;
  }
`;

const ImagePreviews = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: ${({ theme }) => theme.radius.sm};
  overflow: hidden;
  border: 2px solid ${({ $dropTarget, theme }) => $dropTarget ? theme.colors.primary : theme.colors.borderStrong};
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  transition: border-color 0.15s, opacity 0.15s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  .remove-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 24px;
    height: 24px;
    padding: 0;
    font-size: 16px;
    line-height: 1;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border: none;
    border-radius: ${({ theme }) => theme.radius.sm};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #8a1c12;
    }
  }

  .order-badge {
    position: absolute;
    bottom: 2px;
    left: 2px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border-radius: 4px;
    pointer-events: none;
  }
`;

function looksLikeImageFile(file) {
  if (file.type.startsWith("image/")) return true;
  // Drag-and-drop from Desktop / some OS folders omits MIME type.
  if (!file.type && /\.(jpe?g|png|webp|gif|bmp|avif)(\?|$)/i.test(file.name)) return true;
  return false;
}

const MAX_PX = 1200;
const JPEG_QUALITY = 0.78;

function compressImage(file) {
  const url = URL.createObjectURL(file);
  const cleanup = () => URL.revokeObjectURL(url);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => {
      cleanup();
      reject(new Error("Image decode failed"));
    };
    img.onload = async () => {
      try {
        if (img.decode) {
          await img.decode();
        }
        let { width, height } = img;
        if (!width || !height) {
          cleanup();
          reject(new Error("Invalid image dimensions"));
          return;
        }
        if (width > MAX_PX || height > MAX_PX) {
          if (width >= height) {
            height = Math.round((height * MAX_PX) / width);
            width = MAX_PX;
          } else {
            width = Math.round((width * MAX_PX) / height);
            height = MAX_PX;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          cleanup();
          reject(new Error("Canvas not available"));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        cleanup();
        resolve(dataUrl);
      } catch (e) {
        cleanup();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.src = url;
  });
}

function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    sessionStorage.getItem(ADMIN_AUTH_KEY) === '1' && Boolean(sessionStorage.getItem(ADMIN_PASSWORD_KEY))
  );
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [bikes, setBikes] = useState([]);
  const [editingId, setEditingId] = useState(() => {
    const draft = readDraft();
    return draft ? draft.editingId : null;
  });
  const [form, setForm] = useState(() => {
    const draft = readDraft();
    return draft ? draft.form : { ...EMPTY_FORM };
  });
  const [componentInput, setComponentInput] = useState(() => {
    const draft = readDraft();
    return draft ? draft.componentInput : "";
  });
  const [imageError, setImageError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [toolsMessage, setToolsMessage] = useState("");
  const importInputRef = useRef(null);
  const draggedImage = useRef(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const handleImageDragStart = useCallback((e, index) => {
    draggedImage.current = index;
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleImageDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedImage.current !== null && draggedImage.current !== index) {
      setDropTargetIndex(index);
    }
  }, []);

  const handleImageDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetIndex(null);
    }
  }, []);

  const handleImageDrop = useCallback((e, toIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndex = draggedImage.current;
    setDropTargetIndex(null);
    draggedImage.current = null;
    if (fromIndex === null || fromIndex === toIndex) return;
    setForm((f) => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(fromIndex, 1);
      imgs.splice(toIndex, 0, moved);
      return { ...f, images: imgs };
    });
  }, []);

  const handleImageDragEnd = useCallback(() => {
    draggedImage.current = null;
    setDropTargetIndex(null);
  }, []);

  useEffect(() => {
    writeDraft(form, componentInput, editingId);
  }, [form, componentInput, editingId]);

  useEffect(() => {
    let isMounted = true;
    fetchAllBikesForAdmin(config.bikes).then((loadedBikes) => {
      if (isMounted) {
        setBikes(loadedBikes);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    const authenticated = await authenticateAdmin(password);
    if (authenticated) {
      sessionStorage.setItem(ADMIN_AUTH_KEY, '1');
      sessionStorage.setItem(ADMIN_PASSWORD_KEY, password);
      setIsAuthenticated(true);
      return;
    }

    setLoginError('Incorrect password or admin API is not configured.');
  };

  if (!isAuthenticated) {
    return (
      <PageWrapper>
        <PageSeo
          title="Admin | Basement Bike Mechanic"
          description="Staff login for Basement Bike Mechanic."
          path="/admin"
          noindex
        />
        <Header />
        <AdminContent>
          <PageTitle>Admin Login</PageTitle>
          <LoginForm onSubmit={handleLogin}>
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoComplete="current-password"
            />
            {loginError && <div className="error">{loginError}</div>}
            <Button type="submit">Log in</Button>
          </LoginForm>
        </AdminContent>
        <Footer onBack={() => navigate("/")} />
      </PageWrapper>
    );
  }

  const nextId = () => {
    const ids = bikes.map((b) => b.id);
    return ids.length ? Math.max(...ids) + 1 : 0;
  };

  const getAdminPassword = () => sessionStorage.getItem(ADMIN_PASSWORD_KEY) || password;

  const persistBikes = async (updated) => {
    const adminPassword = getAdminPassword();
    if (!adminPassword) {
      sessionStorage.removeItem(ADMIN_AUTH_KEY);
      setIsAuthenticated(false);
      throw new Error("Please log in again before saving to the database.");
    }

    const saved = await saveBikesToDatabase(updated, adminPassword);
    setBikes(saved);
    return saved;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setImageError("");
    if (!form.name.trim()) return;
    if (form.images.length === 0) {
      setImageError("Please add at least one image.");
      return;
    }

    const existingBike = editingId !== null ? bikes.find((b) => b.id === editingId) : null;
    const bike = {
      id: editingId !== null ? editingId : nextId(),
      name: form.name.trim(),
      images: [...form.images],
      price: parseInt(form.price, 10) || 0,
      status: existingBike?.status || "available",
      description: form.description.trim(),
      frame_size: form.frame_size.trim(),
      wheel_size: form.wheel_size.trim(),
      components: form.components.filter((c) => c.trim()),
    };

    let updated;
    if (editingId !== null) {
      updated = bikes.map((b) => (b.id === editingId ? bike : b));
    } else {
      updated = [...bikes, bike];
    }

    try {
      await persistBikes(updated);
      setForm({ ...EMPTY_FORM });
      setComponentInput("");
      setEditingId(null);
      clearDraft();
    } catch (err) {
      setImageError(`Database save failed: ${err.message}`);
    }
  };

  const processFiles = async (files) => {
    if (files.length === 0) return;
    setImageError("");
    const imageFiles = files.filter(looksLikeImageFile);
    const skippedNonImages = files.length - imageFiles.length;

    const messageParts = [];
    if (skippedNonImages > 0) {
      messageParts.push("Some files were skipped (not images).");
    }

    const added = [];
    let failed = 0;
    for (const file of imageFiles) {
      try {
        added.push(await compressImage(file));
      } catch {
        failed += 1;
      }
    }
    if (added.length > 0) {
      setForm((f) => ({ ...f, images: [...f.images, ...added] }));
    }
    if (failed > 0) {
      messageParts.push(
        failed === imageFiles.length
          ? "Failed to read one or more images."
          : `${failed} image(s) could not be processed; others were added.`
      );
    }
    if (messageParts.length > 0) {
      setImageError(messageParts.join(" "));
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    await processFiles(files);
  };

  const removeImage = (index) => {
    setForm((f) => ({
      ...f,
      images: f.images.filter((_, i) => i !== index),
    }));
  };

  const handleEdit = (bike) => {
    setForm({
      name: bike.name,
      images: [...(bike.images || [])],
      price: String(bike.price),
      description: bike.description || "",
      frame_size: bike.frame_size || "",
      wheel_size: bike.wheel_size || "",
      components: Array.isArray(bike.components) ? [...bike.components] : [],
    });
    setComponentInput("");
    setEditingId(bike.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this bike listing?")) return;
    const updated = bikes.filter((b) => b.id !== id);
    try {
      await persistBikes(updated);
      if (editingId === id) {
        setForm({ ...EMPTY_FORM });
        setComponentInput("");
        setEditingId(null);
        clearDraft();
      }
    } catch (err) {
      setToolsMessage(`Delete failed: ${err.message}`);
    }
  };

  const handleCancelEdit = () => {
    setForm({ ...EMPTY_FORM });
    setComponentInput("");
    setEditingId(null);
    setImageError("");
    clearDraft();
  };

  const handleToggleSold = async (id) => {
    const bike = bikes.find((b) => b.id === id);
    if (!bike) return;
    const newStatus = bike.status === "sold" ? "available" : "sold";
    const updated = bikes.map((b) => (b.id === id ? { ...b, status: newStatus } : b));
    try {
      await persistBikes(updated);
    } catch (err) {
      setToolsMessage(`Status update failed: ${err.message}`);
    }
  };

  const addComponent = () => {
    const trimmed = componentInput.trim();
    if (!trimmed) return;
    setForm((f) => ({ ...f, components: [...f.components, trimmed] }));
    setComponentInput("");
  };

  const removeComponent = (index) => {
    setForm((f) => ({ ...f, components: f.components.filter((_, i) => i !== index) }));
  };

  const handleComponentKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addComponent();
    }
  };

  const handleExportBikes = () => {
    setToolsMessage("");
    const blob = new Blob([JSON.stringify(bikes, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `basementbikemechanic-bikes-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setToolsMessage("Bike listings exported.");
  };

  const handleSaveCurrentToDatabase = async () => {
    setToolsMessage("");
    try {
      const saved = await saveBikesToDatabase(bikes, getAdminPassword());
      setBikes(saved);
      setToolsMessage(`Saved ${saved.length} bike listing${saved.length === 1 ? "" : "s"} to the database.`);
    } catch (err) {
      setToolsMessage(`Database save failed: ${err.message}`);
    }
  };

  const handleImportClick = () => {
    setToolsMessage("");
    importInputRef.current?.click();
  };

  const handleImportBikes = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      const imported = JSON.parse(await file.text());
      if (!Array.isArray(imported)) {
        setToolsMessage("Import failed. Choose a bike listings JSON array.");
        return;
      }

      const normalized = imported.map((bike, index) => ({
        id: Number.isFinite(Number(bike.id)) ? Number(bike.id) : index,
        name: typeof bike.name === "string" ? bike.name : "",
        images: Array.isArray(bike.images) ? bike.images.filter((src) => typeof src === "string") : [],
        price: parseInt(bike.price, 10) || 0,
        status: bike.status === "sold" ? "sold" : "available",
        description: typeof bike.description === "string" ? bike.description : "",
        frame_size: typeof bike.frame_size === "string" ? bike.frame_size : "",
        wheel_size: typeof bike.wheel_size === "string" ? bike.wheel_size : "",
        components: Array.isArray(bike.components) ? bike.components.filter((c) => typeof c === "string") : [],
      }));

      if (bikes.length > 0 && !window.confirm("Replace the current bike listings with this import?")) {
        return;
      }

      const saved = await saveBikesToDatabase(normalized, getAdminPassword());
      saveBikes(saved);
      setBikes(saved);
      setForm({ ...EMPTY_FORM });
      setComponentInput("");
      setEditingId(null);
      clearDraft();
      setToolsMessage(`Imported ${saved.length} bike listing${saved.length === 1 ? "" : "s"}.`);
    } catch (err) {
      setToolsMessage(`Import failed: ${err.message}`);
    }
  };

  return (
    <PageWrapper>
      <PageSeo
        title="Admin – Bikes for Sale | Basement Bike Mechanic"
        description="Manage used bike listings for Basement Bike Mechanic."
        path="/admin"
        noindex
      />
      <Header />
      <AdminContent>
        <PageTitle>Admin – Bikes for Sale</PageTitle>
        <BikeForm onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="bike-name">Bike name</label>
            <input
              id="bike-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Trek Domane SL 6"
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Images</label>
            <DropZone
              $dragging={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
              />
              <span className="drop-icon">📷</span>
              <span>
                <strong>Click to browse</strong> or drag &amp; drop images here
              </span>
              <span style={{ fontSize: "0.8rem" }}>Keep file sizes reasonable to avoid storage limits.</span>
            </DropZone>
            {form.images.length > 1 && (
              <small>Drag images to reorder. The first image is the main listing photo.</small>
            )}
            {form.images.length > 0 && (
              <ImagePreviews>
                {form.images.map((src, i) => (
                  <ImagePreview
                    key={i}
                    draggable
                    $dragging={draggedImage.current === i}
                    $dropTarget={dropTargetIndex === i}
                    onDragStart={(e) => handleImageDragStart(e, i)}
                    onDragOver={(e) => handleImageDragOver(e, i)}
                    onDragLeave={handleImageDragLeave}
                    onDrop={(e) => handleImageDrop(e, i)}
                    onDragEnd={handleImageDragEnd}
                  >
                    <img src={src} alt={`Preview ${i + 1}`} />
                    <span className="order-badge">{i + 1}</span>
                    <button className="remove-btn" type="button" onClick={() => removeImage(i)} aria-label="Remove image">
                      ×
                    </button>
                  </ImagePreview>
                ))}
              </ImagePreviews>
            )}
            {imageError && <small style={{ color: "#b90000" }}>{imageError}</small>}
          </FormGroup>
          <FormGroup>
            <label htmlFor="bike-price">Price ($)</label>
            <input
              id="bike-price"
              type="number"
              min="0"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="300"
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="bike-description">Description</label>
            <textarea
              id="bike-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describe the bike's condition, history, or anything a buyer should know…"
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="bike-frame-size">Frame size</label>
            <input
              id="bike-frame-size"
              type="text"
              value={form.frame_size}
              onChange={(e) => setForm((f) => ({ ...f, frame_size: e.target.value }))}
              placeholder='e.g. 56cm, Medium, 17"'
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="bike-wheel-size">Wheel size</label>
            <input
              id="bike-wheel-size"
              type="text"
              value={form.wheel_size}
              onChange={(e) => setForm((f) => ({ ...f, wheel_size: e.target.value }))}
              placeholder="e.g. 700c, 27.5&quot;, 29&quot;"
            />
          </FormGroup>
          <FormGroup>
            <label>Components</label>
            <small>Add notable components like groupset, brakes, wheels, etc.</small>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
              <input
                type="text"
                value={componentInput}
                onChange={(e) => setComponentInput(e.target.value)}
                onKeyDown={handleComponentKeyDown}
                placeholder="e.g. Shimano 105 groupset"
                style={{ flex: 1 }}
              />
              <Button type="button" className="secondary" onClick={addComponent} style={{ whiteSpace: "nowrap" }}>
                + Add
              </Button>
            </div>
            {form.components.length > 0 && (
              <ul style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0" }}>
                {form.components.map((comp, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "0.35rem 0.5rem", borderRadius: "4px", marginBottom: "0.25rem",
                      background: "rgba(0,0,0,0.04)", fontSize: "0.95rem",
                    }}
                  >
                    {comp}
                    <button
                      type="button"
                      onClick={() => removeComponent(i)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#b42318", fontWeight: 700, fontSize: "1.1rem", padding: "0 0.25rem",
                      }}
                      aria-label={`Remove ${comp}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </FormGroup>
          <ButtonRow>
            <Button type="submit">{editingId !== null ? "Update" : "Add"} bike</Button>
            {editingId !== null && (
              <Button type="button" className="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
            )}
          </ButtonRow>
        </BikeForm>

        <SectionTitle>Current listings</SectionTitle>
        <AdminTools>
          <Button type="button" className="secondary" onClick={handleExportBikes} disabled={bikes.length === 0}>
            Export listings
          </Button>
          <Button
            type="button"
            className="secondary"
            onClick={handleSaveCurrentToDatabase}
            disabled={bikes.length === 0}
          >
            Save to database
          </Button>
          <Button type="button" className="secondary" onClick={handleImportClick}>
            Import listings
          </Button>
          <HiddenFileInput
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportBikes}
          />
          {toolsMessage && <ToolMessage>{toolsMessage}</ToolMessage>}
        </AdminTools>
        {bikes.length === 0 ? (
          <EmptyBikes>No bikes listed yet. Add one above.</EmptyBikes>
        ) : (
          <BikeList>
            {bikes.map((bike) => (
              <BikeListItem key={bike.id}>
                <div className="bike-info">
                  <div className="bike-name">
                    {bike.name || '(Untitled)'}
                    {bike.status === "sold" ? <SoldBadge>Sold</SoldBadge> : <AvailableBadge>Available</AvailableBadge>}
                  </div>
                  <div className="bike-price">${bike.price} · {bike.images.length} image(s)</div>
                </div>
                <div className="bike-actions">
                  <Button
                    type="button"
                    className={bike.status === "sold" ? "secondary" : "danger"}
                    onClick={() => handleToggleSold(bike.id)}
                    style={{ fontSize: "0.85rem", padding: "0.4rem 0.75rem" }}
                  >
                    {bike.status === "sold" ? "Mark Available" : "Mark Sold"}
                  </Button>
                  <Button type="button" className="secondary" onClick={() => handleEdit(bike)}>
                    Edit
                  </Button>
                  <Button type="button" className="danger" onClick={() => handleDelete(bike.id)}>
                    Delete
                  </Button>
                </div>
              </BikeListItem>
            ))}
          </BikeList>
        )}
      </AdminContent>
      <Footer onBack={() => navigate("/")} />
    </PageWrapper>
  );
}

export default Admin;
